import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import {
  type CreateTrialMedicationWindowRequest,
  type TrialMedicationWindowDto,
  type UpdateTrialMedicationWindowRequest,
} from '@ctms/contracts';
import { AlertCircle, X } from 'lucide-react';
import { z } from 'zod';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { trialQueryKeys } from '../trials.query-keys.js';
import {
  type MedicationWindowLang,
  type MedicationWindowMessages,
  medicationWindowTexts,
} from './medication-windows.i18n.js';

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

const MAX_MEDICATION_WINDOWS = 5;

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

/** Whole-day count (non-negative integer). Blank max is `undefined` at the call site. */
export function toDayCount(value: string): number | undefined {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed < 0) {
    return undefined;
  }
  return parsed;
}

export interface MedicationWindowFormInput {
  name: string;
  minDays: string;
  maxDays: string;
}

export function buildMedicationWindowSchema(messages: MedicationWindowMessages) {
  return z
    .object({
      name: z.string().trim().min(1, messages.nameRequired).max(255, messages.nameRequired),
      minDays: z.string().min(1, messages.minDaysRequired),
      maxDays: z.string(),
    })
    .superRefine((value, ctx) => {
      if (toDayCount(value.minDays) === undefined) {
        ctx.addIssue({ code: 'custom', path: ['minDays'], message: messages.minDaysInvalid });
      }
      if (value.maxDays.trim() !== '' && toDayCount(value.maxDays) === undefined) {
        ctx.addIssue({ code: 'custom', path: ['maxDays'], message: messages.maxDaysInvalid });
      }
    });
}

function prefill(window: TrialMedicationWindowDto | null): MedicationWindowFormInput {
  if (window) {
    return {
      name: window.name,
      minDays: String(window.minDays),
      maxDays: window.maxDays === null ? '' : String(window.maxDays),
    };
  }
  return { name: '', minDays: '', maxDays: '' };
}

export function toCreateMedicationWindowRequest(
  data: MedicationWindowFormInput,
): CreateTrialMedicationWindowRequest {
  const maxDays = data.maxDays.trim() === '' ? undefined : toDayCount(data.maxDays);
  const request: CreateTrialMedicationWindowRequest = {
    name: data.name.trim(),
    minDays: toDayCount(data.minDays) ?? 0,
  };
  if (maxDays !== undefined) {
    request.maxDays = maxDays;
  }
  return request;
}

export function toUpdateMedicationWindowRequest(
  data: MedicationWindowFormInput,
): UpdateTrialMedicationWindowRequest {
  const maxDays = data.maxDays.trim() === '' ? null : toDayCount(data.maxDays);
  return {
    name: data.name.trim(),
    minDays: toDayCount(data.minDays) ?? 0,
    maxDays,
  };
}

export function formatMedicationWindowRange(
  window: TrialMedicationWindowDto,
  messages: MedicationWindowMessages,
): string {
  const min = `${messages.minDays}: ${window.minDays}`;
  return window.maxDays === null ? min : `${min} · ${messages.maxDays}: ${window.maxDays}`;
}

interface MedicationWindowFormProps {
  trialId: number;
  window: TrialMedicationWindowDto | null;
  messages: MedicationWindowMessages;
  onCancel: () => void;
  onSaved: () => void;
}

function MedicationWindowForm({
  trialId,
  window,
  messages,
  onCancel,
  onSaved,
}: MedicationWindowFormProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const schema = buildMedicationWindowSchema(messages);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<MedicationWindowFormInput>({
    resolver: zodResolver(schema),
    defaultValues: prefill(window),
  });

  const onSubmit = async (data: MedicationWindowFormInput): Promise<void> => {
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      if (window) {
        await trialsApi.updateMedicationWindow(trialId, window.id, toUpdateMedicationWindowRequest(data));
      } else {
        await trialsApi.createMedicationWindow(trialId, toCreateMedicationWindowRequest(data));
      }
      onSaved();
    } catch (err: unknown) {
      setErrorMessage(await getApiErrorMessage(err, messages.unknownError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4 rounded-lg border border-slate-800 p-4" noValidate>
      {errorMessage && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}

      <div>
        <label htmlFor="medication-window-name" className={labelClass}>{messages.name}</label>
        <input
          id="medication-window-name"
          type="text"
          disabled={isSubmitting}
          className={fieldClass}
          placeholder={messages.namePlaceholder}
          {...register('name')}
        />
        {errors.name && <p className="mt-1 text-xs text-rose-400">{errors.name.message}</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="medication-window-min" className={labelClass}>{messages.minDays}</label>
          <input
            id="medication-window-min"
            type="text"
            inputMode="numeric"
            disabled={isSubmitting}
            className={fieldClass}
            placeholder="9"
            {...register('minDays')}
          />
          {errors.minDays && <p className="mt-1 text-xs text-rose-400">{errors.minDays.message}</p>}
        </div>
        <div>
          <label htmlFor="medication-window-max" className={labelClass}>{messages.maxDays}</label>
          <input
            id="medication-window-max"
            type="text"
            inputMode="numeric"
            disabled={isSubmitting}
            className={fieldClass}
            placeholder="—"
            {...register('maxDays')}
          />
          <p className="mt-1 text-xs text-slate-500">{messages.maxDaysOptionalHint}</p>
          {errors.maxDays && <p className="mt-1 text-xs text-rose-400">{errors.maxDays.message}</p>}
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-slate-800 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
        >
          {messages.cancel}
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
        >
          {isSubmitting ? messages.saving : messages.save}
        </button>
      </div>
    </form>
  );
}

export interface MedicationWindowsDialogProps {
  trialId: number;
  canWrite: boolean;
  lang?: MedicationWindowLang;
  onClose: () => void;
}

export function MedicationWindowsDialog({
  trialId,
  canWrite,
  lang = 'pl',
  onClose,
}: MedicationWindowsDialogProps): JSX.Element {
  const messages = medicationWindowTexts(lang);
  const queryClient = useQueryClient();
  const [editingWindow, setEditingWindow] = useState<TrialMedicationWindowDto | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const windowsQuery = useQuery({
    queryKey: trialQueryKeys.medicationWindows(trialId),
    queryFn: () => trialsApi.listMedicationWindows(trialId),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const windows = windowsQuery.data ?? [];
  const atMax = windows.length >= MAX_MEDICATION_WINDOWS;

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: trialQueryKeys.medicationWindows(trialId) });
  };

  const handleSaved = (): void => {
    invalidate();
    setEditingWindow(null);
    setFormOpen(false);
  };

  const startAdd = (): void => {
    setEditingWindow(null);
    setFormOpen(true);
  };

  const startEdit = (window: TrialMedicationWindowDto): void => {
    setEditingWindow(window);
    setFormOpen(true);
  };

  const handleDelete = async (window: TrialMedicationWindowDto): Promise<void> => {
    setDeletingId(window.id);
    try {
      await trialsApi.deleteMedicationWindow(trialId, window.id);
      invalidate();
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="medication-windows-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-slate-800 pb-4">
          <div>
            <h2 id="medication-windows-title" className="text-base font-semibold text-slate-100">
              {messages.dialogTitle}
            </h2>
            <p className="mt-1 text-xs text-slate-400">{messages.dialogSubtitle}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={messages.close}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {windowsQuery.isLoading ? (
          <p className="py-6 text-sm text-slate-400">…</p>
        ) : windowsQuery.isError ? (
          <div role="alert" className="py-6 text-sm text-rose-300">
            {messages.unknownError}
          </div>
        ) : windows.length === 0 && !formOpen ? (
          <p className="py-6 text-sm text-slate-400">{messages.noWindows}</p>
        ) : (
          <ul className="mt-4 space-y-2">
            {windows.map((window) => (
              <li
                key={window.id}
                className="flex items-center justify-between gap-3 rounded-md border border-slate-800 px-3 py-2"
              >
                <div className="min-w-0">
                  <span className="text-sm text-slate-100">{window.name}</span>
                  <span className="ml-2 text-xs text-slate-400">
                    {formatMedicationWindowRange(window, messages)}
                  </span>
                </div>
                {canWrite && (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => startEdit(window)}
                      className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800"
                    >
                      {messages.editWindow}
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleDelete(window)}
                      disabled={deletingId === window.id}
                      className="rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                    >
                      {deletingId === window.id ? messages.deleting : messages.deleteWindow}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        {formOpen && (
          <MedicationWindowForm
            trialId={trialId}
            window={editingWindow}
            messages={messages}
            onCancel={() => {
              setEditingWindow(null);
              setFormOpen(false);
            }}
            onSaved={handleSaved}
          />
        )}

        {canWrite && !formOpen && (
          <div className="mt-4">
            <button
              type="button"
              onClick={startAdd}
              disabled={atMax}
              className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
            >
              {messages.addWindow}
            </button>
            {atMax && <p className="mt-2 text-xs text-slate-500">{messages.maxReached}</p>}
          </div>
        )}
      </div>
    </div>
  );
}